import {
  getFirstAvailableBooking,
  mapFirstAvailableFreeturnMs,
} from '@/common/apis/services/booking/getFirstAvailableBooking';
import { CENTERS } from '@/common/types/centers';
import { getFreeturnDisplayText } from '@/modules/booking/utils/getFreeturnDisplayText';
import { applyOnlineVisitPatientDisplayBufferMs } from '@/modules/booking/utils/onlineVisitPatientDisplayTime';
import { describeOnlineVisitStartFa } from '@/modules/profile/utils/formatOnlineVisitApproxTime';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

interface UseInvoiceNoticeFreeturnParams {
  serviceId: string;
  userCenterId?: string;
  isUserCenterLoading: boolean;
}

export const useInvoiceNoticeFreeturn = ({
  serviceId,
  userCenterId,
  isUserCenterLoading,
}: UseInvoiceNoticeFreeturnParams) => {
  const hasRequiredParams = Boolean(serviceId && userCenterId);

  const firstAvailableQuery = useQuery({
    queryKey: ['firstAvailableBooking', CENTERS.CONSULT, serviceId, userCenterId],
    queryFn: () =>
      getFirstAvailableBooking({
        center_id: CENTERS.CONSULT,
        service_id: serviceId,
        user_center_id: userCenterId!,
      }),
    enabled: hasRequiredParams,
    staleTime: 0,
    cacheTime: 0,
    retry: (failureCount, error) => axios.isAxiosError(error) && !error.response && failureCount < 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const freeturnMs = mapFirstAvailableFreeturnMs(firstAvailableQuery.data);

  const isFreeturnLoading = isUserCenterLoading || (hasRequiredParams && firstAvailableQuery.isLoading);

  // Same wording as the profile's «شروع ویزیت آنلاین …» button the patient just tapped:
  // «حدود 20 دقیقه دیگر», «امروز حدود 3:30 عصر», «فردا حدود 9 صبح».
  const nowMs = Date.now();
  const freeturnText =
    freeturnMs == null
      ? 'در اسرع وقت'
      : describeOnlineVisitStartFa(applyOnlineVisitPatientDisplayBufferMs(Math.max(freeturnMs, nowMs)), nowMs);

  return {
    freeturnText,
    /** Current live wording («امروز حدود ساعت 14:15») — the A/B control group keeps it. */
    legacyFreeturnText: getFreeturnDisplayText(freeturnMs),
    isFreeturnLoading,
  };
};
